import {
  Box,
  Button,
  Flex,
  List,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { isMobile } from "react-device-detect";
import SVG from "react-inlinesvg";
import { useTranslation } from "react-i18next";
import AppSelectOption from "@/features/line-sheet-sets/app-select-option";
import SearchInput from "@/shared/components/search-input";

/**
 * 모바일에서 AppSelect의 팝오버 드롭다운을 대체하는 전체화면 시트.
 *
 * 왜 팝오버를 못 쓰는가 — 카카오톡 인앱브라우저가 알림톡/봇 대화방 링크로 열리는
 * "바텀시트" 모드에서는 가상 키보드가 떠도 뷰포트를 전혀 조정하지 않는다.
 * 실측(Android): 키보드가 372px을 덮는데 innerHeight·100vh·visualViewport.height 는
 * 그대로이고(오히려 756→792로 늘어난다) offsetTop만 65px을 보고한다. 즉 JS로 키보드
 * 높이를 알 방법이 없고, OS가 포커스된 입력창을 밀어올려 주지도 않는다(adjustNothing).
 *
 * 그래서 브라우저 협조 없이 성립하는 배치는 하나뿐이다 —
 * **검색 인풋을 화면 최상단에 정적으로 고정하고 결과를 그 바로 아래에 두는 것.**
 * 키보드 위 가용 높이는 약 420px이라 헤더 48 + 검색 44를 빼고도 7행 이상이 보인다.
 *
 * 지켜야 할 것:
 *  - 검색 인풋은 헤더 바로 아래 고정. 아래로 내려가는 순간 키보드에 가려진다.
 *  - 하단 고정 푸터 금지. 다중선택 "확인"을 푸터에 두면 키보드 뒤로 들어가므로 헤더에 둔다.
 *  - 자동 포커스 금지. 열자마자 키보드가 뜨면 목록을 보기도 전에 화면이 잘린다.
 */
interface AppSelectMobileSheetProps<T> {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** 이미 검색어로 걸러진 목록 (AppSelect의 emptyQueryAddedFilteredOptions) */
  options: AppSelectOption<T>[];
  queryValue: string;
  onQueryChange: (value: string) => void;
  idFunc: (t: T) => any;
  calcIsActive: (option: AppSelectOption<T>) => boolean;
  onClickOption: (option: AppSelectOption<T>) => void;
  isDisabled: boolean;
  isMultiple: boolean;
  requestComponent?: React.ReactNode;
  /** showEmptyDataOption으로 앞쪽에 끼워 넣은 "결과 없음" 항목 수 — 그만큼 회색 처리한다. */
  emptyStyledCount?: number;
}

// 모바일 터치 타깃. AppSelectOption의 기본값(p="8px 16px", nowrap)은 데스크톱 기준이라
// 손가락으로 누르기엔 좁고, 긴 이름이 잘린다.
const MOBILE_ITEM_STYLE: React.CSSProperties = {
  padding: "12px 16px",
  minHeight: "44px",
  whiteSpace: "normal",
  textAlign: "left",
};

export default function AppSelectMobileSheet<T>({
  isOpen,
  onClose,
  title,
  options,
  queryValue,
  onQueryChange,
  idFunc,
  calcIsActive,
  onClickOption,
  isDisabled,
  isMultiple,
  requestComponent,
  emptyStyledCount = 0,
}: AppSelectMobileSheetProps<T>) {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"full"}
      // 열자마자 포커스가 잡히면(기본값은 첫 포커스 가능 요소 = 닫기 버튼) 기기에 따라
      // 키보드가 올라온다. 사용자가 검색창을 직접 탭할 때만 뜨게 한다.
      autoFocus={false}
      returnFocusOnClose={false}
      // allowPinchZoom: Chakra의 스크롤 락이 두 손가락 제스처까지 취소해서, 모바일에서
      // 화면이 확대되면 축소로 빠져나올 수 없다. custom-modal/index.tsx:45 와 같은 이유.
      allowPinchZoom={isMobile}
      motionPreset={"slideInBottom"}
    >
      <ModalOverlay />
      <ModalContent
        margin={0}
        borderRadius={0}
        height={"100vh"}
        maxHeight={"100vh"}
        display={"flex"}
        flexDirection={"column"}
        overflow={"hidden"}
      >
        <Flex
          flexShrink={0}
          height={"48px"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"8px"}
          padding={"0 8px"}
          borderBottom={"0.5px solid #D4D4D1"}
          background={"#F7F9FA"}
        >
          <Button
            variant={"ghost"}
            minWidth={"44px"}
            height={"44px"}
            padding={"0"}
            color={"#444440"}
            onClick={onClose}
            aria-label={t("close")}
          >
            <SVG src={"/icons-v1_1/x.svg"} width={"20px"} height={"20px"} />
          </Button>
          <Text
            fontSize={"14px"}
            fontWeight={700}
            noOfLines={1}
            textAlign={"center"}
          >
            {title}
          </Text>
          {/* 다중선택은 선택할 때마다 부모 상태가 곧바로 갱신되므로(AppSelect.handleClick)
              이 버튼은 확정이 아니라 닫기다. 그래도 "다 골랐다"는 출구가 있어야 한다.
              푸터가 아니라 헤더에 두는 이유는 하단이 키보드에 가려지기 때문. */}
          {isMultiple ? (
            <Button
              variant={"ghost"}
              minWidth={"44px"}
              height={"44px"}
              padding={"0 8px"}
              fontSize={"14px"}
              fontWeight={700}
              color={"#1272EF"}
              onClick={onClose}
            >
              {t("confirm")}
            </Button>
          ) : (
            <Box minWidth={"44px"} />
          )}
        </Flex>

        <Box
          flexShrink={0}
          padding={"8px"}
          borderBottom={"0.5px solid #D4D4D1"}
        >
          <SearchInput
            value={queryValue}
            onChange={onQueryChange}
            placeholder={t("search")}
            inputProps={{
              // iOS는 16px 미만 입력창에 포커스하면 화면을 확대해 좌표계가 틀어진다.
              // forgot-password-step2-form.tsx:178 과 같은 회피.
              fontSize: "16px",
              minHeight: "44px",
              autoFocus: false,
              inputMode: "search",
              enterKeyHint: "search",
              autoComplete: "off",
            }}
          />
        </Box>

        <List
          flex={"1"}
          minHeight={0}
          overflowY={"auto"}
          overscrollBehavior={"contain"}
          display={"flex"}
          flexDirection={"column"}
          fontSize={"14px"}
        >
          {options.length === 0 && (
            <Flex
              paddingY={"32px"}
              flexDirection={"column"}
              alignItems={"center"}
              gap={"12px"}
            >
              <Flex flexDirection={"column"} alignItems={"center"}>
                <Box color={"#D4D4D1"}>
                  <SVG
                    src={"/icons-v1_1/warning-triangle-solid.svg"}
                    width={28}
                    height={28}
                  />
                </Box>
                <Text color={"#6F6F6C"}>{t("no_results")}</Text>
              </Flex>
              {requestComponent}
            </Flex>
          )}
          {options.map((option, index) => (
            <AppSelectOption<T>
              key={idFunc(option.value)}
              option={option}
              isActive={calcIsActive(option)}
              handleClick={onClickOption}
              isDisabled={isDisabled}
              itemStyle={
                index < emptyStyledCount
                  ? { ...MOBILE_ITEM_STYLE, color: "#C0C0C0" }
                  : MOBILE_ITEM_STYLE
              }
            />
          ))}
        </List>
      </ModalContent>
    </Modal>
  );
}
